// Iter 62ao — Standalone "widget" calendar page — optimized for iOS home-screen
// PWA installation. Add ASA to home screen → new icon → tap opens this page in
// fullscreen. Shows today + tomorrow as a UNIFIED timeline (Calendly RDVs +
// ASA tasks mixed and sorted by hour), following user request (Iter 62ao).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { completeElevateTask } from '../hooks/useElevate';
import { Calendar, Video, ChevronRight, CheckSquare } from '../components/icons';

export default function WidgetCalendarPage() {
  const { user, isFounder } = useAuth();
  const nav = useNavigate();
  const [calendly, setCalendly] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [elevateTasks, setElevateTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Iter 62ak — Custom document title so iOS uses "ASA · Calendrier" when Hugo
  // adds the widget URL to his home screen (Safari picks up document.title).
  useEffect(() => {
    const prev = document.title;
    document.title = 'ASA · Calendrier';
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const promises = [
        api.get('/tasks').then((r) => r.data).catch(() => []),
        // Iter 62cj — Calendly for every user (personal OAuth or founder PAT fallback)
        api.get('/calendly/events?days_ahead=7').then((r) => r.data?.events || []).catch(() => []),
      ];
      if (isFounder) {
        promises.push(api.get('/elevate/tasks').then((r) => r.data?.tasks || []).catch(() => []));
      } else {
        promises.push(Promise.resolve([]));
      }
      const [t, c, el] = await Promise.all(promises);
      if (!cancelled) {
        setTasks(t);
        setCalendly(c);
        setElevateTasks(el);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isFounder]);

  // Iter 62ay — Cocher une tâche Elevate + optimistic update
  const handleCompleteElevate = async (taskId, status) => {
    setElevateTasks((arr) => arr.map((t) => t.id === taskId ? { ...t, status } : t));
    try {
      const r = await completeElevateTask(taskId, status);
      if (!r.remote_synced) {
        toast.success('Coché dans ASA (Elevate offline)');
      } else {
        toast.success('Sync Elevate ✓');
      }
      if (navigator.vibrate) navigator.vibrate(15);
    } catch (e) {
      toast.error('Erreur');
      // Revert
      setElevateTasks((arr) => arr.map((t) => t.id === taskId ? { ...t, status: status === 'done' ? 'todo' : 'done' } : t));
    }
  };

  // Today / tomorrow boundaries (local time)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfter = new Date(today);
  dayAfter.setDate(dayAfter.getDate() + 2);

  // Build unified timeline items → { time: Date, kind: 'rdv'|'task', payload: ... }
  const buildItems = (fromDate, toDate) => {
    const items = [];
    // Calendly events
    calendly.forEach((e) => {
      if (!e.start_time) return;
      const d = new Date(e.start_time);
      if (d >= fromDate && d < toDate) {
        items.push({ time: d, kind: 'rdv', payload: e });
      }
    });
    // Tasks assigned to me
    tasks.forEach((t) => {
      if (!t.due_date) return;
      const assignedToMe =
        t.assigned_to === user?.id
        || t.assigned_to_derush === user?.id
        || t.assigned_to_montage === user?.id
        || (t.assignees || []).includes(user?.id);
      if (!assignedToMe) return;
      if (t.stage === 'done' || t.done_at) return;
      const d = new Date(t.due_date);
      // A task's due_date can be date-only ('2026-01-15') → treat as noon so it
      // sorts naturally within the day timeline rather than at 00:00.
      if (t.due_date.length <= 10) d.setHours(12, 0, 0, 0);
      if (d >= fromDate && d < toDate) {
        items.push({ time: d, kind: 'task', payload: t });
      }
    });
    // Iter 62ax — Elevate Influence tasks (external SaaS, Bearer API).
    // Row shape from backend: {due_date: '2026-06-17', due_time: '10:00', client_name, ...}
    elevateTasks.forEach((t) => {
      if (!t.due_date) return;
      const iso = `${t.due_date}T${t.due_time || '12:00'}:00`;
      const d = new Date(iso);
      if (isNaN(d.getTime())) return;
      if (d >= fromDate && d < toDate) {
        items.push({ time: d, kind: 'elevate', payload: t });
      }
    });
    return items.sort((a, b) => a.time - b.time);
  };

  const todayItems = buildItems(today, tomorrow);
  const tomorrowItems = buildItems(tomorrow, dayAfter);

  // Iter 62ap — Overdue section (ASA + Elevate tasks whose due_date is before today).
  // Sorted by time DESC (most recent first) and capped for readability.
  const overdueItems = (() => {
    const items = [];
    tasks.forEach((t) => {
      if (!t.due_date) return;
      const assignedToMe =
        t.assigned_to === user?.id
        || t.assigned_to_derush === user?.id
        || t.assigned_to_montage === user?.id
        || (t.assignees || []).includes(user?.id);
      if (!assignedToMe) return;
      if (t.stage === 'done' || t.done_at) return;
      const d = new Date(t.due_date);
      if (t.due_date.length <= 10) d.setHours(12, 0, 0, 0);
      if (d < today) items.push({ time: d, kind: 'task', payload: t });
    });
    elevateTasks.forEach((t) => {
      if (!t.due_date) return;
      const iso = `${t.due_date}T${t.due_time || '12:00'}:00`;
      const d = new Date(iso);
      if (!isNaN(d.getTime()) && d < today) items.push({ time: d, kind: 'elevate', payload: t });
    });
    return items.sort((a, b) => b.time - a.time).slice(0, 10);
  })();

  const dayLabel = today.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(140% 90% at 50% -10%, rgba(46,163,242,0.10) 0%, transparent 55%), var(--bg)',
      paddingBottom: 40,
    }}>
      {/* Fake iOS-status-bar spacer (safe area) */}
      <div style={{
        height: 'env(safe-area-inset-top, 44px)',
        background: 'transparent',
      }} />

      <div style={{ padding: '18px 20px 12px' }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
          color: '#F5C56A', letterSpacing: '0.16em',
          textTransform: 'uppercase',
        }}>Widget · ASA</div>
        <div style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 26,
          color: 'var(--t1)', marginTop: 4, letterSpacing: '-0.02em',
          textTransform: 'capitalize',
        }}>{dayLabel}</div>
      </div>

      {loading ? (
        <div style={{ padding: '0 20px' }}>
          <div className="shimmer" style={{ height: 100, borderRadius: 14, marginBottom: 10 }} />
          <div className="shimmer" style={{ height: 100, borderRadius: 14 }} />
        </div>
      ) : (
        <>
          {/* Iter 62ap — Overdue items surface at the top with a red accent so
              Hugo sees Elevate + ASA delays first-thing when opening the widget. */}
          {overdueItems.length > 0 && (
            <TimelineSection
              title="En retard"
              accent="#FF6B6B"
              items={overdueItems}
              onOpen={() => nav('/tasks')}
              overdue
              nav={nav}
              onCompleteElevate={handleCompleteElevate}
            />
          )}

          {/* Unified timeline — Today */}
          <TimelineSection
            title="Aujourd'hui"
            accent="#F5C56A"
            items={todayItems}
            onOpen={() => nav('/planning')}
            emptyText="Rien de prévu — journée libre."
            nav={nav}
            onCompleteElevate={handleCompleteElevate}
          />

          {/* Unified timeline — Tomorrow */}
          {tomorrowItems.length > 0 && (
            <TimelineSection
              title="Demain"
              accent="#8A9DB8"
              items={tomorrowItems}
              onOpen={() => nav('/planning')}
              muted
              nav={nav}
              onCompleteElevate={handleCompleteElevate}
            />
          )}

          {/* Empty state — nothing today & nothing tomorrow & nothing overdue */}
          {todayItems.length === 0 && tomorrowItems.length === 0 && overdueItems.length === 0 && (
            <div style={{
              margin: '20px 20px',
              padding: '30px 20px',
              borderRadius: 14,
              background: 'var(--sf)', border: '1px solid var(--bd)',
              textAlign: 'center',
            }}>
              <Calendar size={30} color="var(--t3)" strokeWidth={1.4} />
              <div style={{
                fontFamily: 'Sora', fontWeight: 700, fontSize: 14,
                color: 'var(--t2)', marginTop: 12,
              }}>Rien de prévu aujourd&apos;hui</div>
              <div style={{
                fontFamily: 'Sora', fontWeight: 500, fontSize: 12,
                color: 'var(--t3)', marginTop: 4,
              }}>Journée libre. Profites-en.</div>
            </div>
          )}

          {/* Open full app link */}
          <button
            onClick={() => nav('/')}
            data-testid="widget-open-full"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 8, width: 'calc(100% - 40px)', margin: '20px 20px 0',
              padding: '14px', borderRadius: 14,
              background:
                'linear-gradient(140deg, rgba(245,197,106,0.14), rgba(245,197,106,0.04))',
              border: '1px solid rgba(245,197,106,0.35)',
              color: '#F5C56A',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Ouvrir ASA complet <ChevronRight size={14} />
          </button>
        </>
      )}
    </div>
  );
}

function TimelineSection({ title, accent, items, onOpen, muted, emptyText, nav, onCompleteElevate }) {
  return (
    <div style={{ margin: '0 20px 14px' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        marginBottom: 10, padding: '0 4px',
      }}>
        <span
          aria-hidden
          style={{
            width: 6, height: 6, borderRadius: 999,
            background: accent,
            boxShadow: `0 0 10px ${accent}`,
          }}
        />
        <div style={{
          flex: 1,
          fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
          color: accent, textTransform: 'uppercase', letterSpacing: '0.14em',
        }}>{title}</div>
        <span style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 10,
          color: 'var(--t3)',
        }}>{items.length}</span>
        {onOpen && (
          <button
            onClick={onOpen}
            style={{
              background: 'transparent', border: 'none', color: 'var(--t3)',
              cursor: 'pointer', padding: 0, display: 'grid', placeItems: 'center',
            }}
            data-testid={`widget-section-open-${title.toLowerCase()}`}
          ><ChevronRight size={13} /></button>
        )}
      </div>

      {items.length === 0 ? (
        <div style={{
          padding: '18px 14px',
          borderRadius: 14,
          background: 'var(--sf)', border: '1px solid var(--bd)',
          textAlign: 'center',
          fontFamily: 'Sora', fontWeight: 600, fontSize: 12,
          color: 'var(--t3)', fontStyle: 'italic',
        }}>{emptyText || 'Rien de prévu'}</div>
      ) : (
        <div style={{
          borderRadius: 14,
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          overflow: 'hidden',
        }}>
          {items.map((it, i) => (
            <TimelineRow
              key={`${it.kind}-${it.payload.id || it.payload.uri}-${i}`}
              item={it}
              muted={muted}
              last={i === items.length - 1}
              nav={nav}
              onCompleteElevate={onCompleteElevate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineRow({ item, muted, last, nav, onCompleteElevate }) {
  const hour = item.time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  if (item.kind === 'rdv') {
    const e = item.payload;
    const tier = e.budget_tier;
    const tierColor = tier === 'gros' ? '#34D399' : tier === 'moyen' ? '#F5C56A' : tier === 'petit' ? '#FBBF24' : null;
    const tierLabel = tier === 'gros' ? '+800€' : tier === 'moyen' ? '300-800€' : tier === 'petit' ? '<300€' : null;
    const joinUrl = e.location?.join_url;
    return (
      <div
        data-testid={`widget-cal-${e.uri?.split('/').pop()}`}
        style={{
          padding: '12px 14px',
          display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: last ? 'none' : '1px solid var(--bd)',
          opacity: muted ? 0.75 : 1,
        }}
      >
        <TimeChip hour={hour} accent="#F5C56A" icon={Calendar} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
            color: 'var(--t1)', letterSpacing: '-0.01em',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{e.name}</div>
          <div style={{
            fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
            color: '#5BBEFC', marginTop: 2,
            display: 'flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            <span style={{
              padding: '0.5px 6px', borderRadius: 999,
              background: 'rgba(245,197,106,0.12)', color: '#F5C56A',
              border: '1px solid rgba(245,197,106,0.35)',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 8.5,
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>RDV</span>
            {e.invitee_name || (e.invitees_counter?.active > 0 ? `${e.invitees_counter.active} invité(s)` : '')}
            {tierColor && (
              <span style={{
                padding: '0.5px 6px', borderRadius: 999,
                background: `${tierColor}22`, color: tierColor,
                border: `1px solid ${tierColor}55`,
                fontFamily: 'Sora', fontWeight: 800, fontSize: 8.5,
              }}>{tierLabel}</span>
            )}
          </div>
        </div>
        {joinUrl && !muted && (
          <a
            href={joinUrl}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={`widget-cal-join-${e.uri?.split('/').pop()}`}
            style={{
              padding: '6px 8px', borderRadius: 8,
              background: 'rgba(52,211,153,0.14)',
              border: '1px solid rgba(52,211,153,0.35)',
              color: '#34D399',
              textDecoration: 'none',
              display: 'grid', placeItems: 'center',
              flexShrink: 0,
            }}
          ><Video size={13} strokeWidth={2} /></a>
        )}
      </div>
    );
  }

  // Elevate Influence task
  if (item.kind === 'elevate') {
    const t = item.payload;
    const isDone = t.status === 'done';
    const isOverdue = item.time < new Date() && !muted && !isDone;
    const statusColor = t.status === 'in_progress' ? '#F5A623' : (isDone ? '#34D399' : '#B58BFF');
    const statusLabel = t.status === 'in_progress' ? 'En cours' : (isDone ? 'Fait' : 'À faire');
    return (
      <div
        data-testid={`widget-elevate-${t.id}`}
        style={{
          padding: '12px 14px',
          display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: last ? 'none' : '1px solid var(--bd)',
          opacity: muted || isDone ? 0.55 : 1,
        }}
      >
        <TimeChip hour={hour} accent="#5BBEFC" icon={CheckSquare} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
            color: 'var(--t1)', letterSpacing: '-0.01em',
            textDecoration: isDone ? 'line-through' : 'none',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{t.client_name ? `${t.client_name} — ${t.title}` : t.title}</div>
          <div style={{
            fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
            color: isOverdue ? '#FF6B6B' : 'var(--t3)', marginTop: 2,
            display: 'flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            <span style={{
              padding: '0.5px 6px', borderRadius: 999,
              background: 'rgba(30,95,255,0.14)', color: '#5BBEFC',
              border: '1px solid rgba(30,95,255,0.45)',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 8.5,
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>Elevate</span>
            <span style={{
              padding: '0.5px 6px', borderRadius: 999,
              background: `${statusColor}22`, color: statusColor,
              border: `1px solid ${statusColor}55`,
              fontFamily: 'Sora', fontWeight: 800, fontSize: 8.5,
            }}>{statusLabel}</span>
            {isOverdue && <span style={{ color: '#FF6B6B', fontWeight: 700 }}>En retard</span>}
          </div>
        </div>
        {/* Iter 62ay — Cocher/décocher directement depuis ASA */}
        {onCompleteElevate && (
          <button
            data-testid={`widget-elevate-check-${t.id}`}
            onClick={() => onCompleteElevate(t.id, isDone ? 'todo' : 'done')}
            title={isDone ? 'Remettre à faire' : 'Marquer comme fait'}
            style={{
              width: 28, height: 28, borderRadius: 8,
              display: 'grid', placeItems: 'center', flexShrink: 0,
              background: isDone ? 'rgba(52,211,153,0.20)' : 'transparent',
              border: `1.5px solid ${isDone ? '#34D399' : 'var(--bd)'}`,
              color: isDone ? '#34D399' : 'var(--t3)',
              cursor: 'pointer',
            }}
          >
            {isDone ? '✓' : ''}
          </button>
        )}
      </div>
    );
  }

  // ASA task
  const t = item.payload;
  const isOverdue = item.time < new Date() && !muted;
  return (
    <button
      onClick={() => nav(`/tasks?open=${t.id}`)}
      data-testid={`widget-task-${t.id}`}
      style={{
        padding: '12px 14px',
        display: 'flex', alignItems: 'center', gap: 12,
        borderBottom: last ? 'none' : '1px solid var(--bd)',
        background: 'transparent', border: 'none',
        textAlign: 'left', width: '100%',
        cursor: 'pointer',
        opacity: muted ? 0.75 : 1,
      }}
    >
      <TimeChip hour={hour} accent={isOverdue ? '#FF6B6B' : '#5BBEFC'} icon={CheckSquare} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
          color: 'var(--t1)', letterSpacing: '-0.01em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{t.display_title || t.title}</div>
        <div style={{
          fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
          color: isOverdue ? '#FF6B6B' : 'var(--t3)', marginTop: 2,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{
            padding: '0.5px 6px', borderRadius: 999,
            background: isOverdue ? 'rgba(255,107,107,0.12)' : 'rgba(91,190,252,0.12)',
            color: isOverdue ? '#FF6B6B' : '#5BBEFC',
            border: `1px solid ${isOverdue ? 'rgba(255,107,107,0.35)' : 'rgba(91,190,252,0.35)'}`,
            fontFamily: 'Sora', fontWeight: 800, fontSize: 8.5,
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>{t.kind === 'video' ? 'Vidéo' : 'Tâche'}</span>
          {isOverdue ? 'En retard' : (t.category_tag ? t.category_tag.replace(/_/g, ' ') : '')}
        </div>
      </div>
      <ChevronRight size={13} color="var(--t3)" />
    </button>
  );
}

function TimeChip({ hour, accent, icon: Icon }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      minWidth: 46, padding: '4px 8px', borderRadius: 8,
      background: 'var(--sf2)', border: '1px solid var(--bd)',
      flexShrink: 0,
    }}>
      <Icon size={11} color={accent} strokeWidth={2} />
      <div style={{
        fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
        color: 'var(--t1)', marginTop: 2, letterSpacing: '-0.01em',
        fontVariantNumeric: 'tabular-nums',
      }}>{hour}</div>
    </div>
  );
}
